/**
 * Crow’s Nest paywall vs supplier-add totals.
 *
 * “11 new leads” is not 11 paywalls. Counts come from Express
 * `opportunity-totals` / the unfiltered `totals` on the feed.
 */

export function emptyOpportunityTotals() {
  return {
    opportunities: 0,
    paywallLimit: 0,
    supplierAdded: 0,
    supplierMilestone: 0,
    supplierSignals: 0,
    byType: {
      paywall_limit: 0,
      supplier_added: 0,
      supplier_milestone: 0,
    },
    mixLabel: 'No leads in this range',
  }
}

function countWord(count, singular, plural) {
  return `${count} ${count === 1 ? singular : plural}`
}

/**
 * Sales-facing mix — supplier-only copy never says “paywall”.
 * @param {{ opportunities?: number, paywallLimit?: number, supplierAdded?: number, supplierMilestone?: number }} [totals]
 */
export function formatOpportunityMixLabel(totals = {}) {
  const paywalls = Number(totals.paywallLimit) || 0
  const supplierAdds = Number(totals.supplierAdded) || 0
  const milestones = Number(totals.supplierMilestone) || 0
  const supplierSignals = supplierAdds + milestones
  const total = Number(totals.opportunities) || paywalls + supplierSignals

  if (total <= 0) return 'No leads in this range'

  const parts = []
  if (paywalls > 0) {
    parts.push(countWord(paywalls, 'paywall', 'paywalls'))
  }
  if (supplierAdds > 0) {
    parts.push(countWord(supplierAdds, 'supplier add', 'supplier adds'))
  }
  if (milestones > 0) {
    parts.push(
      countWord(milestones, 'supplier milestone', 'supplier milestones'),
    )
  }

  if (parts.length === 1 && (paywalls === total || supplierSignals === total)) {
    return parts[0]
  }

  if (!parts.length) {
    return countWord(total, 'lead', 'leads')
  }

  return `${countWord(total, 'lead', 'leads')} · ${parts.join(' · ')}`
}

/**
 * @param {object|null|undefined} totals — Express totals blob
 * @returns {ReturnType<typeof emptyOpportunityTotals>|null}
 */
export function mapOpportunityTotals(totals) {
  if (!totals || typeof totals !== 'object') return null

  const byType = totals.byType || {}
  const paywallLimit = Number(totals.paywallLimit ?? byType.paywall_limit) || 0
  const supplierAdded =
    Number(totals.supplierAdded ?? byType.supplier_added) || 0
  const supplierMilestone =
    Number(totals.supplierMilestone ?? byType.supplier_milestone) || 0
  const supplierSignals = supplierAdded + supplierMilestone
  const opportunities =
    Number(totals.opportunities) || paywallLimit + supplierSignals

  const mapped = {
    opportunities,
    paywallLimit,
    supplierAdded,
    supplierMilestone,
    supplierSignals,
    byType: {
      paywall_limit: paywallLimit,
      supplier_added: supplierAdded,
      supplier_milestone: supplierMilestone,
    },
  }
  mapped.mixLabel = formatOpportunityMixLabel(mapped)
  return mapped
}
